import React, { useState, useMemo } from 'react';
import MainLayout from '../../components/ui/MainLayout';
import Icon from '../../components/AppIcon';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRealtime } from '../../contexts/RealtimeContext';
import { useBooking, BUILDINGS_CONFIG } from '../../contexts/BookingContext';
import OccupancyAlertBanner from './components/OccupancyAlertBanner';
import { getRoomOccupancyStatus, getTodayStr } from '../../utils/occupancyStatus';

const ROOM_TYPES = ['Standard', 'Studio', 'Apartment'];

// Cleaning status config
const CLEANING_STATUS_CONFIG = {
  occupied:              { label: 'Occupied',              labelFr: 'Occupée',              dotClass: 'bg-blue-500',    badgeClass: 'bg-blue-100 text-blue-700 border border-blue-200' },
  reserved:              { label: 'Reserved',              labelFr: 'Réservée',             dotClass: 'bg-indigo-500',  badgeClass: 'bg-indigo-100 text-indigo-700 border border-indigo-200' },
  available:             { label: 'Available',             labelFr: 'Disponible',           dotClass: 'bg-emerald-500', badgeClass: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  'needs-housekeeping':  { label: 'Needs Housekeeping',    labelFr: 'Ménage requis',        dotClass: 'bg-amber-500',   badgeClass: 'bg-amber-100 text-amber-700 border border-amber-200' },
  'cleaning-in-progress':{ label: 'Cleaning in Progress',  labelFr: 'Nettoyage en cours',   dotClass: 'bg-purple-500',  badgeClass: 'bg-purple-100 text-purple-700 border border-purple-200' },
  cleaned:               { label: 'Cleaned',               labelFr: 'Nettoyée',             dotClass: 'bg-teal-500',    badgeClass: 'bg-teal-100 text-teal-700 border border-teal-200' },
};

const RESERVATION_STATUS_CONFIG = {
  'active-reservation':  { label: 'Active Reservation', labelFr: 'Réservation active',  badgeClass: 'bg-blue-100 text-blue-700 border border-blue-200' },
  'confirmed-stay':      { label: 'Confirmed Stay',     labelFr: 'Séjour confirmé',      badgeClass: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  'completed-stay':      { label: 'Completed Stay',     labelFr: 'Séjour terminé',       badgeClass: 'bg-slate-100 text-slate-600 border border-slate-200' },
  active:        { label: 'Confirmed Stay',     labelFr: 'Séjour confirmé',      badgeClass: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  pending:       { label: 'Active Reservation', labelFr: 'Réservation active',   badgeClass: 'bg-blue-100 text-blue-700 border border-blue-200' },
  'checked-out': { label: 'Completed Stay',     labelFr: 'Séjour terminé',       badgeClass: 'bg-slate-100 text-slate-600 border border-slate-200' },
};

const CLEANING_FILTER_OPTIONS = ['all', 'occupied', 'reserved', 'available', 'needs-housekeeping', 'cleaning-in-progress', 'cleaned'];

const EMPTY_FORM = { roomNumber: '', building: '', floor: '', type: 'Standard', capacity: 1, notes: '' };

function financialStatusLabel(val, language) {
  if (val === 'covered') return language === 'fr' ? 'Pris en charge par Senghor' : 'Covered by Senghor';
  if (val === 'not-covered') return language === 'fr' ? 'Non pris en charge par Senghor' : 'Not Covered by Senghor';
  return val || '—';
}

function CleaningBadge({ status, language }) {
  const cfg = CLEANING_STATUS_CONFIG?.[status] ?? CLEANING_STATUS_CONFIG?.available;
  const label = language === 'fr' ? cfg?.labelFr : cfg?.label;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg?.badgeClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg?.dotClass}`} />
      {label}
    </span>
  );
}

function ReservationStatusBadge({ status, language }) {
  const cfg = RESERVATION_STATUS_CONFIG?.[status];
  if (!cfg) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${cfg?.badgeClass}`}>
      {language === 'fr' ? cfg?.labelFr : cfg?.label}
    </span>
  );
}

function StatCard({ icon, label, value, accent }) {
  return (
    <div className="stat-card flex items-center gap-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: accent + '18', border: `1px solid ${accent}30` }}>
        <Icon name={icon} size={20} style={{ color: accent }} />
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</p>
      </div>
    </div>
  );
}

// ── Room Detail Drawer ─────────────────────────────────────────────────────────
function RoomDetailDrawer({ room, stay, claims, language, onClose }) {
  const checkInDate = stay?.check_in_date || stay?.checkIn ? new Date(stay?.check_in_date || stay?.checkIn) : null;
  const checkOutDate = stay?.check_out_date || stay?.checkOut ? new Date(stay?.check_out_date || stay?.checkOut) : null;
  const lengthOfStay = checkInDate && checkOutDate
    ? Math.round((checkOutDate - checkInDate) / (1000 * 60 * 60 * 24))
    : null;

  const roomClaims = claims?.filter(c => c?.room === room?.roomNumber || c?.room === room?.room_number) ?? [];

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Drawer */}
      <div className="w-full max-w-md bg-card border-l border-border shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Icon name="DoorOpen" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-base font-bold text-foreground" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{room?.roomNumber || room?.room_number}</h2>
              <p className="text-xs text-muted-foreground">{room?.building} · {room?.type || room?.room_type} · {language === 'fr' ? 'Capacité' : 'Capacity'}: {room?.capacity}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Guest Information */}
          {stay ? (
            <>
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="User" size={14} className="text-primary" />
                  <h3 className="text-sm font-bold text-foreground">{language === 'fr' ? 'Informations du résident' : 'Guest Information'}</h3>
                </div>
                <div className="bg-muted/30 rounded-xl p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="User" size={16} className="text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{stay?.resident_name || stay?.residentName}</p>
                      <p className="text-xs text-muted-foreground font-mono">{stay?.resident_id_code || stay?.residentId}</p>
                    </div>
                  </div>
                  {(stay?.email) && (
                    <div className="flex items-center gap-2 text-xs">
                      <Icon name="Mail" size={12} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-muted-foreground truncate">{stay?.email}</span>
                    </div>
                  )}
                  {(stay?.department) && (
                    <div className="flex items-center gap-2 text-xs">
                      <Icon name="Building2" size={12} className="text-muted-foreground flex-shrink-0" />
                      <span className="text-foreground font-medium">{stay?.department}</span>
                    </div>
                  )}
                  {(stay?.financial_status || stay?.financialStatus) && (
                    <div className="flex items-center gap-2 text-xs">
                      <Icon name="CreditCard" size={12} className="text-muted-foreground flex-shrink-0" />
                      <span className={`font-medium ${(stay?.financial_status || stay?.financialStatus) === 'covered' ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {financialStatusLabel(stay?.financial_status || stay?.financialStatus, language)}
                      </span>
                    </div>
                  )}
                </div>
              </section>

              {/* Stay Information */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="CalendarDays" size={14} className="text-primary" />
                  <h3 className="text-sm font-bold text-foreground">{language === 'fr' ? 'Informations du séjour' : 'Stay Information'}</h3>
                </div>
                <div className="bg-muted/30 rounded-xl p-4 space-y-2.5">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{language === 'fr' ? 'Chambre' : 'Room Number'}</span>
                    <span className="font-semibold text-foreground font-mono">{stay?.room_number || stay?.room}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{language === 'fr' ? 'Arrivée' : 'Check-in Date'}</span>
                    <span className="font-medium text-foreground">{stay?.check_in_date || stay?.checkIn}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{language === 'fr' ? 'Départ' : 'Check-out Date'}</span>
                    <span className="font-medium text-foreground">{stay?.check_out_date || stay?.checkOut}</span>
                  </div>
                  {lengthOfStay !== null && (
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{language === 'fr' ? 'Durée du séjour' : 'Length of Stay'}</span>
                      <span className="font-medium text-foreground">{lengthOfStay} {language === 'fr' ? 'jours' : 'days'}</span>
                    </div>
                  )}
                  {(stay?.accommodation_type || stay?.accommodationType) && (
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">{language === 'fr' ? 'Type' : 'Accommodation Type'}</span>
                      <span className="font-medium text-foreground capitalize">{(stay?.accommodation_type || stay?.accommodationType)?.replace('-', ' ')}</span>
                    </div>
                  )}
                </div>
              </section>

              {/* Reservation Details */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="ClipboardList" size={14} className="text-primary" />
                  <h3 className="text-sm font-bold text-foreground">{language === 'fr' ? 'Détails de la réservation' : 'Reservation Details'}</h3>
                </div>
                <div className="bg-muted/30 rounded-xl p-4 space-y-2.5">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">{language === 'fr' ? 'Statut de réservation' : 'Booking Status'}</span>
                    <ReservationStatusBadge status={stay?.status} language={language} />
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-muted-foreground">{language === 'fr' ? 'Statut du résident' : 'Guest Status'}</span>
                    <span className="font-medium text-foreground capitalize">{(stay?.guest_status || stay?.guestStatus)?.replace('-', ' ') || '—'}</span>
                  </div>
                </div>
              </section>
            </>
          ) : (
            <div className="bg-muted/30 rounded-xl p-6 text-center">
              <Icon name="UserX" size={24} className="text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Aucun résident actuellement dans cette chambre' : 'No current guest in this room'}</p>
            </div>
          )}

          {/* Claims / Incidents */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="AlertTriangle" size={14} className="text-amber-500" />
              <h3 className="text-sm font-bold text-foreground">{language === 'fr' ? 'Réclamations / Incidents' : 'Claims / Incidents'}</h3>
              {roomClaims?.length > 0 && (
                <span className="ml-auto px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">{roomClaims?.length}</span>
              )}
            </div>
            {roomClaims?.length === 0 ? (
              <div className="bg-muted/30 rounded-xl p-4 text-center">
                <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Aucun incident signalé' : 'No incidents reported'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {roomClaims?.map((claim, idx) => (
                  <div key={idx} className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                    <div className="flex items-start justify-between mb-1.5">
                      <p className="text-xs font-semibold text-amber-800">{claim?.guest_name || claim?.guestName || '—'}</p>
                      <span className="text-[10px] text-amber-600">{claim?.report_date || claim?.reportDate}</span>
                    </div>
                    <p className="text-xs text-amber-700 leading-relaxed">{claim?.description}</p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Room Notes */}
          {(room?.notes) && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="StickyNote" size={14} className="text-muted-foreground" />
                <h3 className="text-sm font-bold text-foreground">{language === 'fr' ? 'Notes' : 'Room Notes'}</h3>
              </div>
              <div className="bg-muted/30 rounded-xl p-4">
                <p className="text-xs text-muted-foreground leading-relaxed">{room?.notes}</p>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function RoomManagement() {
  const { t, language } = useLanguage();
  const realtime = useRealtime();
  const booking = useBooking();

  // Use Supabase-backed rooms from RealtimeContext
  const rooms = realtime?.rooms ?? [];
  const stays = realtime?.stays ?? [];
  const claims = realtime?.claims ?? [];
  const getRoomStatus = realtime?.getRoomStatus ?? booking?.getRoomStatus;

  // Housekeeping derived data
  const roomsNeedingHousekeeping = useMemo(() => rooms?.filter(r => {
    const status = getRoomStatus?.(r?.room_number || r?.roomNumber);
    return status === 'needs-housekeeping';
  }), [rooms, getRoomStatus]);

  const roomsCleaningInProgress = useMemo(() => rooms?.filter(r => {
    const cs = r?.cleaning_status;
    return cs === 'cleaning-in-progress';
  }), [rooms]);

  const today = new Date()?.toISOString()?.split('T')?.[0];
  const todayCheckOuts = useMemo(() => stays?.filter(s => {
    const co = s?.check_out_date || s?.checkOut;
    return co === today && (s?.status === 'confirmed-stay' || s?.status === 'active-reservation');
  }), [stays, today]);

  const [filterBuilding, setFilterBuilding] = useState('all');
  const [filterFloor, setFilterFloor] = useState('all');
  const [filterCleaningStatus, setFilterCleaningStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const [modalMode, setModalMode] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [drawerRoom, setDrawerRoom] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const floorOptions = useMemo(() => {
    if (filterBuilding === 'all') return [];
    return BUILDINGS_CONFIG?.[filterBuilding]?.floors?.map(f => ({ value: f?.floor, label: f?.label })) ?? [];
  }, [filterBuilding]);

  const formFloorOptions = useMemo(() => {
    if (!form?.building) return [];
    return BUILDINGS_CONFIG?.[form?.building]?.floors?.map(f => ({ value: f?.floor, label: f?.label })) ?? [];
  }, [form?.building]);

  const formRoomOptions = useMemo(() => {
    if (!form?.building || form?.floor === '') return [];
    const floorData = BUILDINGS_CONFIG?.[form?.building]?.floors?.find(f => f?.floor === Number(form?.floor));
    return floorData?.rooms ?? [];
  }, [form?.building, form?.floor]);

  // Enrich rooms with live status + guest info — date-driven
  const enrichedRooms = useMemo(() => {
    const today = getTodayStr();
    return rooms?.map(r => {
      const rNum = r?.room_number || r?.roomNumber;
      // Date-driven occupancy status
      const occupancyStatus = getRoomOccupancyStatus(rNum, stays, r, today);
      // Map to cleaning_status values for display
      let cleaningStatus;
      if (occupancyStatus === 'Occupied')    cleaningStatus = 'occupied';
      else if (occupancyStatus === 'Reserved')   cleaningStatus = 'reserved';
      else if (occupancyStatus === 'Maintenance') cleaningStatus = 'maintenance';
      else {
        // Fall back to DB cleaning_status for housekeeping states
        const dbStatus = getRoomStatus?.(rNum) || r?.cleaning_status || 'available';
        cleaningStatus = (dbStatus === 'occupied' || dbStatus === 'reserved') ? 'available' : dbStatus;
      }
      const activeStay = stays?.find(s => {
        const sRn = (s?.room_number || s?.room || '')?.trim();
        if (sRn !== rNum) return false;
        const ci = s?.check_in_date || s?.checkIn || '';
        const co = s?.check_out_date || s?.checkOut || '';
        // Active stay = confirmed-stay within date window OR active-reservation
        return (s?.status === 'confirmed-stay' || s?.status === 'active') && today >= ci && today < co ||
               (s?.status === 'active-reservation' || s?.status === 'pending');
      });
      return {
        ...r,
        roomNumber: rNum,
        type: r?.room_type || r?.type || 'Standard',
        floor: r?.floor ?? 0,
        cleaningStatus,
        currentGuest: activeStay?.resident_name || activeStay?.residentName || null,
        checkOutDate: activeStay?.check_out_date || activeStay?.checkOut || null,
        reservationStatus: activeStay?.status || null,
        activeStay: activeStay || null,
      };
    });
  }, [rooms, stays, getRoomStatus]);

  const filtered = useMemo(() => {
    return enrichedRooms?.filter(r => {
      if (filterBuilding !== 'all' && r?.building !== filterBuilding) return false;
      if (filterFloor !== 'all' && r?.floor !== Number(filterFloor)) return false;
      if (filterCleaningStatus !== 'all' && r?.cleaningStatus !== filterCleaningStatus) return false;
      if (filterType !== 'all' && r?.type !== filterType) return false;
      if (search && !r?.roomNumber?.toLowerCase()?.includes(search?.toLowerCase())) return false;
      return true;
    });
  }, [enrichedRooms, filterBuilding, filterFloor, filterCleaningStatus, filterType, search]);

  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);
  const paginated = filtered?.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const stats = useMemo(() => ({
    total: enrichedRooms?.length,
    available: enrichedRooms?.filter(r => r?.cleaningStatus === 'available')?.length,
    occupied: enrichedRooms?.filter(r => r?.cleaningStatus === 'occupied')?.length,
    needsHousekeeping: roomsNeedingHousekeeping?.length,
  }), [enrichedRooms, roomsNeedingHousekeeping]);

  const openAdd = () => { setForm(EMPTY_FORM); setFormError(''); setModalMode('add'); };
  const openEdit = (room) => {
    setSelectedRoom(room);
    setForm({ roomNumber: room?.roomNumber, building: room?.building, floor: String(room?.floor), type: room?.type, capacity: room?.capacity, notes: room?.notes || '' });
    setFormError('');
    setModalMode('edit');
  };
  const openDelete = (room) => { setSelectedRoom(room); setModalMode('delete'); };
  const closeModal = () => { setModalMode(null); setSelectedRoom(null); setForm(EMPTY_FORM); setFormError(''); };

  const handleFormChange = (field, value) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'building') { updated.floor = ''; updated.roomNumber = ''; }
      if (field === 'floor') { updated.roomNumber = ''; }
      return updated;
    });
  };

  const flash = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(''), 3000); };

  const handleSave = async () => {
    if (!form?.building || form?.floor === '' || !form?.roomNumber) {
      setFormError(language === 'fr' ? 'Le bâtiment, l\'étage et le numéro de chambre sont obligatoires.' : 'Building, floor, and room number are required.');
      return;
    }
    setSaving(true);
    setFormError('');
    if (modalMode === 'add') {
      const duplicate = rooms?.find(r => (r?.room_number || r?.roomNumber) === form?.roomNumber);
      if (duplicate) {
        setFormError(language === 'fr' ? 'Ce numéro de chambre existe déjà.' : 'This room number already exists.');
        setSaving(false);
        return;
      }
      const { error } = await realtime?.addRoom?.({
        roomNumber: form?.roomNumber,
        building: form?.building,
        floor: Number(form?.floor),
        type: form?.type,
        capacity: Number(form?.capacity),
        notes: form?.notes,
      });
      if (error) {
        setFormError(error?.message || 'Failed to add room. Please try again.');
        setSaving(false);
        return;
      }
      flash(language === 'fr' ? `Chambre ${form?.roomNumber} ajoutée avec succès.` : `Room ${form?.roomNumber} added successfully.`);
    } else {
      const { error } = await realtime?.updateRoom?.(selectedRoom?.id, {
        roomNumber: form?.roomNumber,
        building: form?.building,
        floor: Number(form?.floor),
        type: form?.type,
        capacity: Number(form?.capacity),
        notes: form?.notes,
      });
      if (error) {
        setFormError(error?.message || 'Failed to update room. Please try again.');
        setSaving(false);
        return;
      }
      flash(language === 'fr' ? `Chambre ${form?.roomNumber} mise à jour avec succès.` : `Room ${form?.roomNumber} updated successfully.`);
    }
    setSaving(false);
    closeModal();
  };

  const handleDelete = async () => {
    setSaving(true);
    const { error } = await realtime?.deleteRoom?.(selectedRoom?.id);
    if (error) {
      setFormError(error?.message || 'Failed to delete room.');
      setSaving(false);
      return;
    }
    flash(language === 'fr' ? `Chambre ${selectedRoom?.roomNumber} supprimée.` : `Room ${selectedRoom?.roomNumber} deleted.`);
    setSaving(false);
    closeModal();
  };

  const handleBuildingFilterChange = (val) => { setFilterBuilding(val); setFilterFloor('all'); setPage(1); };

  const handleCleaningAction = async (roomNumber, newStatus) => {
    await realtime?.updateRoomCleaningStatus?.(roomNumber, null, newStatus);
    flash(language === 'fr' ? `Statut de la chambre ${roomNumber} mis à jour.` : `Room ${roomNumber} status updated.`);
  };

  const openDrawer = (room) => setDrawerRoom(room);
  const closeDrawer = () => setDrawerRoom(null);

  const selectClass = "w-full px-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-foreground";

  return (
    <MainLayout>
      <div className="space-y-5">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="page-header-title">{t?.rooms?.title || 'Room Management'}</h1>
            <p className="text-sm text-muted-foreground mt-1">
              {language === 'fr' ? 'Gérez toutes les chambres — statuts en temps réel depuis les enregistrements.' : 'Manage all rooms — statuses update in real-time from check-ins/outs.'}
            </p>
          </div>
          <button
            onClick={openAdd}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90 flex-shrink-0"
            style={{ background: 'var(--color-primary)', color: '#fff' }}
          >
            <Icon name="Plus" size={16} />
            {t?.rooms?.addRoom || 'Add Room'}
          </button>
        </div>

        {/* Success toast */}
        {successMsg && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium"
            style={{ background: 'rgba(26,122,74,0.1)', border: '1px solid rgba(26,122,74,0.2)', color: 'var(--color-success)' }}>
            <Icon name="CheckCircle2" size={16} />
            {successMsg}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard icon="DoorOpen" label={language === 'fr' ? 'Total chambres' : 'Total Rooms'} value={stats?.total} accent="#1B2D5B" />
          <StatCard icon="CheckCircle2" label={language === 'fr' ? 'Disponibles' : 'Available'} value={stats?.available} accent="#1A7A4A" />
          <StatCard icon="Users" label={language === 'fr' ? 'Occupées' : 'Occupied'} value={stats?.occupied} accent="#2563EB" />
          <StatCard icon="Sparkles" label={language === 'fr' ? 'Ménage requis' : 'Housekeeping Needed'} value={stats?.needsHousekeeping} accent="#D97706" />
        </div>

        {/* Occupancy Threshold Alerts */}
        <OccupancyAlertBanner />

        {/* Housekeeping Alert Banner */}
        {(roomsNeedingHousekeeping?.length > 0 || roomsCleaningInProgress?.length > 0) && (
          <div className="premium-card p-4 border-l-4" style={{ borderLeftColor: '#D97706' }}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Sparkles" size={18} className="text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-foreground mb-1">
                  {language === 'fr' ? 'Tableau de bord Housekeeping' : 'Housekeeping Dashboard'}
                </h3>
                <div className="flex flex-wrap gap-4 text-sm">
                  {roomsNeedingHousekeeping?.length > 0 && (
                    <span className="flex items-center gap-1.5 text-amber-700">
                      <span className="w-2 h-2 rounded-full bg-amber-500" />
                      <strong>{roomsNeedingHousekeeping?.length}</strong> {language === 'fr' ? 'chambre(s) nécessitent un nettoyage' : 'room(s) need cleaning'}
                    </span>
                  )}
                  {roomsCleaningInProgress?.length > 0 && (
                    <span className="flex items-center gap-1.5 text-purple-700">
                      <span className="w-2 h-2 rounded-full bg-purple-500" />
                      <strong>{roomsCleaningInProgress?.length}</strong> {language === 'fr' ? 'nettoyage en cours' : 'cleaning in progress'}
                    </span>
                  )}
                  {todayCheckOuts?.length > 0 && (
                    <span className="flex items-center gap-1.5 text-rose-700">
                      <span className="w-2 h-2 rounded-full bg-rose-500" />
                      <strong>{todayCheckOuts?.length}</strong> {language === 'fr' ? 'départ(s) aujourd\'hui' : 'check-out(s) today'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="premium-card p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">{t?.common?.search || 'Search'}</label>
              <div className="relative">
                <Icon name="Search" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="e.g. F1-201"
                  value={search}
                  onChange={e => { setSearch(e?.target?.value); setPage(1); }}
                  className="w-full pl-9 pr-3 py-2.5 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all text-foreground placeholder-muted-foreground"
                />
              </div>
            </div>

            <div className="min-w-[120px]">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">{t?.rooms?.building || 'Building'}</label>
              <select value={filterBuilding} onChange={e => handleBuildingFilterChange(e?.target?.value)} className={selectClass}>
                <option value="all">{t?.rooms?.allBuildings || 'All Buildings'}</option>
                {Object.keys(BUILDINGS_CONFIG)?.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>

            <div className="min-w-[120px]">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">{t?.rooms?.floor || 'Floor'}</label>
              <select value={filterFloor} onChange={e => { setFilterFloor(e?.target?.value); setPage(1); }} disabled={filterBuilding === 'all'} className={selectClass + ' disabled:opacity-50'}>
                <option value="all">{language === 'fr' ? 'Tous' : 'All'}</option>
                {floorOptions?.map(f => <option key={f?.value} value={f?.value}>{f?.label}</option>)}
              </select>
            </div>

            <div className="min-w-[180px]">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">{language === 'fr' ? 'Statut nettoyage' : 'Cleaning Status'}</label>
              <select value={filterCleaningStatus} onChange={e => { setFilterCleaningStatus(e?.target?.value); setPage(1); }} className={selectClass}>
                <option value="all">{language === 'fr' ? 'Tous les statuts' : 'All Statuses'}</option>
                {CLEANING_FILTER_OPTIONS?.slice(1)?.map(s => (
                  <option key={s} value={s}>{language === 'fr' ? CLEANING_STATUS_CONFIG?.[s]?.labelFr : CLEANING_STATUS_CONFIG?.[s]?.label}</option>
                ))}
              </select>
            </div>

            <div className="min-w-[120px]">
              <label className="block text-xs font-semibold text-muted-foreground mb-1.5 uppercase tracking-wide">{t?.rooms?.type || 'Type'}</label>
              <select value={filterType} onChange={e => { setFilterType(e?.target?.value); setPage(1); }} className={selectClass}>
                <option value="all">{t?.rooms?.allTypes || 'All Types'}</option>
                {ROOM_TYPES?.map(tp => <option key={tp} value={tp}>{tp}</option>)}
              </select>
            </div>

            {(filterBuilding !== 'all' || filterFloor !== 'all' || filterCleaningStatus !== 'all' || filterType !== 'all' || search) && (
              <button
                onClick={() => { setFilterBuilding('all'); setFilterFloor('all'); setFilterCleaningStatus('all'); setFilterType('all'); setSearch(''); setPage(1); }}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 text-sm text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                <Icon name="X" size={14} />
                {language === 'fr' ? 'Réinitialiser' : 'Reset'}
              </button>
            )}
          </div>

          {/* Quick filter chips */}
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
            <span className="text-xs text-muted-foreground font-medium self-center">{language === 'fr' ? 'Filtre rapide :' : 'Quick filter:'}</span>
            {[
              { key: 'needs-housekeeping', label: language === 'fr' ? 'Ménage requis' : 'Needs Cleaning', color: 'bg-amber-100 text-amber-700 border-amber-200' },
              { key: 'cleaning-in-progress', label: language === 'fr' ? 'En cours' : 'In Progress', color: 'bg-purple-100 text-purple-700 border-purple-200' },
              { key: 'cleaned', label: language === 'fr' ? 'Nettoyées' : 'Cleaned', color: 'bg-teal-100 text-teal-700 border-teal-200' },
              { key: 'available', label: language === 'fr' ? 'Disponibles' : 'Available', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
            ]?.map(chip => (
              <button
                key={chip?.key}
                onClick={() => { setFilterCleaningStatus(filterCleaningStatus === chip?.key ? 'all' : chip?.key); setPage(1); }}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${filterCleaningStatus === chip?.key ? chip?.color + ' ring-2 ring-offset-1 ring-current' : 'bg-muted text-muted-foreground border-border hover:border-primary/40'}`}
              >
                {chip?.label}
              </button>
            ))}
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            {language === 'fr' ? 'Affichage de' : 'Showing'} <span className="font-semibold text-foreground">{filtered?.length}</span> {language === 'fr' ? 'sur' : 'of'} <span className="font-semibold text-foreground">{rooms?.length}</span> {language === 'fr' ? 'chambres' : 'rooms'}
          </p>
        </div>

        {/* Table */}
        <div className="premium-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{language === 'fr' ? 'Chambre' : 'Room'}</th>
                  <th>{t?.rooms?.building || 'Building'}</th>
                  <th>{t?.rooms?.floor || 'Floor'}</th>
                  <th className="hidden sm:table-cell">{t?.rooms?.type || 'Type'}</th>
                  <th className="hidden md:table-cell">{language === 'fr' ? 'Résident actuel' : 'Current Guest'}</th>
                  <th className="hidden lg:table-cell">{language === 'fr' ? 'Date de départ' : 'Check-out Date'}</th>
                  <th className="hidden xl:table-cell">{language === 'fr' ? 'Statut réservation' : 'Reservation Status'}</th>
                  <th>{language === 'fr' ? 'Statut nettoyage' : 'Cleaning Status'}</th>
                  <th className="text-right">{t?.rooms?.actions || 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {paginated?.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-16 text-center">
                      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mx-auto mb-3">
                        <Icon name="SearchX" size={22} className="text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground">{language === 'fr' ? 'Aucune chambre ne correspond aux filtres actuels.' : 'No rooms match the current filters.'}</p>
                    </td>
                  </tr>
                ) : (
                  paginated?.map(room => (
                    <tr key={room?.id}>
                      <td>
                        <button
                          onClick={() => openDrawer(room)}
                          className="font-semibold text-primary hover:underline hover:text-primary/80 transition-colors"
                          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8125rem' }}
                          title={language === 'fr' ? 'Voir les détails de la chambre' : 'View room details'}
                        >
                          {room?.roomNumber}
                        </button>
                      </td>
                      <td>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-muted text-xs font-bold text-foreground">{room?.building}</span>
                      </td>
                      <td className="text-muted-foreground text-sm">{room?.floor === 0 ? (language === 'fr' ? 'RDC' : 'Ground') : `F${room?.floor}`}</td>
                      <td className="hidden sm:table-cell text-muted-foreground text-sm">{room?.type}</td>
                      <td className="hidden md:table-cell">
                        {room?.currentGuest ? (
                          <span className="text-sm font-medium text-foreground">{room?.currentGuest}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">—</span>
                        )}
                      </td>
                      <td className="hidden lg:table-cell">
                        {room?.checkOutDate ? (
                          <span className="text-sm text-muted-foreground font-mono">{room?.checkOutDate}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground/50">—</span>
                        )}
                      </td>
                      <td className="hidden xl:table-cell">
                        {room?.reservationStatus ? (
                          <ReservationStatusBadge status={room?.reservationStatus} language={language} />
                        ) : (
                          <span className="text-xs text-muted-foreground/50">—</span>
                        )}
                      </td>
                      <td>
                        <CleaningBadge status={room?.cleaningStatus} language={language} />
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1">
                          {room?.cleaningStatus === 'needs-housekeeping' && (
                            <button
                              onClick={() => handleCleaningAction(room?.roomNumber, 'cleaning-in-progress')}
                              className="p-1.5 rounded-lg text-purple-500 hover:bg-purple-50 transition-colors"
                              title={language === 'fr' ? 'Démarrer le nettoyage' : 'Start Cleaning'}
                            >
                              <Icon name="Sparkles" size={14} />
                            </button>
                          )}
                          {room?.cleaningStatus === 'cleaning-in-progress' && (
                            <button
                              onClick={() => handleCleaningAction(room?.roomNumber, 'available')}
                              className="p-1.5 rounded-lg text-teal-500 hover:bg-teal-50 transition-colors"
                              title={language === 'fr' ? 'Marquer comme nettoyée' : 'Mark as Cleaned'}
                            >
                              <Icon name="CheckCircle2" size={14} />
                            </button>
                          )}
                          {(room?.cleaningStatus === 'available' || room?.cleaningStatus === 'cleaned') && (
                            <span className="p-1.5 text-emerald-400">
                              <Icon name="CheckCircle2" size={14} />
                            </span>
                          )}
                          <button
                            onClick={() => openDrawer(room)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/8 transition-colors"
                            title={language === 'fr' ? 'Voir les détails' : 'View Details'}
                          >
                            <Icon name="PanelRightOpen" size={14} />
                          </button>
                          <button onClick={() => openEdit(room)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/8 transition-colors"
                            title={t?.common?.edit || 'Edit'}>
                            <Icon name="Pencil" size={14} />
                          </button>
                          <button onClick={() => openDelete(room)}
                            className="p-1.5 rounded-lg text-muted-foreground hover:text-error hover:bg-error/8 transition-colors"
                            title={t?.common?.delete || 'Delete'}>
                            <Icon name="Trash2" size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
              <p className="text-xs text-muted-foreground">{language === 'fr' ? 'Page' : 'Page'} <span className="font-semibold text-foreground">{page}</span> {language === 'fr' ? 'sur' : 'of'} {totalPages}</p>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 transition-colors">
                  <Icon name="ChevronLeft" size={14} />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                  const p = start + i;
                  return (
                    <button key={p} onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${p === page ? 'text-white' : 'border border-border text-muted-foreground hover:bg-muted'}`}
                      style={p === page ? { background: 'var(--color-primary)' } : {}}>
                      {p}
                    </button>
                  );
                })}
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 transition-colors">
                  <Icon name="ChevronRight" size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Room Detail Drawer ── */}
      {drawerRoom && (
        <RoomDetailDrawer
          room={drawerRoom}
          stay={drawerRoom?.activeStay}
          claims={claims}
          language={language}
          onClose={closeDrawer}
        />
      )}

      {/* ── Add / Edit Modal ── */}
      {(modalMode === 'add' || modalMode === 'edit') && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <h2 className="text-base font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {modalMode === 'add' ? (language === 'fr' ? 'Ajouter une chambre' : 'Add New Room') : `${language === 'fr' ? 'Modifier' : 'Edit'} — ${selectedRoom?.roomNumber}`}
              </h2>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm"
                  style={{ background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.2)', color: 'var(--color-error)' }}>
                  <Icon name="AlertCircle" size={15} />
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.rooms?.building || 'Building'} <span className="text-error">*</span></label>
                <select value={form?.building} onChange={e => handleFormChange('building', e?.target?.value)} className={selectClass}>
                  <option value="">{language === 'fr' ? 'Sélectionner un bâtiment…' : 'Select building…'}</option>
                  {Object.keys(BUILDINGS_CONFIG)?.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.rooms?.floor || 'Floor'} <span className="text-error">*</span></label>
                <select value={form?.floor} onChange={e => handleFormChange('floor', e?.target?.value)} disabled={!form?.building} className={selectClass + ' disabled:opacity-50'}>
                  <option value="">{language === 'fr' ? 'Sélectionner un étage…' : 'Select floor…'}</option>
                  {formFloorOptions?.map(f => <option key={f?.value} value={f?.value}>{f?.label}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.rooms?.roomNumber || 'Room Number'} <span className="text-error">*</span></label>
                {modalMode === 'add' ? (
                  <select value={form?.roomNumber} onChange={e => handleFormChange('roomNumber', e?.target?.value)} disabled={!form?.building || form?.floor === ''} className={selectClass + ' disabled:opacity-50'}>
                    <option value="">{language === 'fr' ? 'Sélectionner une chambre…' : 'Select room…'}</option>
                    {formRoomOptions?.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                ) : (
                  <input type="text" value={form?.roomNumber} onChange={e => handleFormChange('roomNumber', e?.target?.value)} className={selectClass} />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">{t?.rooms?.type || 'Type'}</label>
                  <select value={form?.type} onChange={e => handleFormChange('type', e?.target?.value)} className={selectClass}>
                    {ROOM_TYPES?.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">{t?.rooms?.capacity || 'Capacity'}</label>
                  <input type="number" min={1} max={6} value={form?.capacity} onChange={e => handleFormChange('capacity', e?.target?.value)} className={selectClass} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t?.rooms?.notes || 'Notes'}</label>
                <textarea rows={3} value={form?.notes} onChange={e => handleFormChange('notes', e?.target?.value)}
                  placeholder={language === 'fr' ? 'Notes optionnelles sur cette chambre…' : 'Optional notes about this room…'}
                  className={selectClass + ' resize-none'} />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
              <button onClick={closeModal} className="px-4 py-2.5 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors">
                {t?.common?.cancel || 'Cancel'}
              </button>
              <button onClick={handleSave} disabled={saving}
                className="px-4 py-2.5 text-sm font-semibold rounded-lg transition-all hover:opacity-90 disabled:opacity-60"
                style={{ background: 'var(--color-primary)', color: '#fff' }}>
                {saving ? (language === 'fr' ? 'Enregistrement…' : 'Saving…') : (modalMode === 'add' ? (t?.rooms?.addRoom || 'Add Room') : (language === 'fr' ? 'Enregistrer' : 'Save Changes'))}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      {modalMode === 'delete' && selectedRoom && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-xl w-full max-w-sm">
            <div className="p-6 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(192,57,43,0.1)', border: '1px solid rgba(192,57,43,0.2)' }}>
                <Icon name="Trash2" size={22} className="text-error" />
              </div>
              <h2 className="text-base font-bold text-foreground mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>{t?.rooms?.deleteRoom || 'Delete Room'}</h2>
              <p className="text-sm text-muted-foreground">
                {language === 'fr'
                  ? `Êtes-vous sûr de vouloir supprimer la chambre `
                  : `Are you sure you want to delete room `}
                <span className="font-semibold text-foreground">{selectedRoom?.roomNumber}</span>
                {language === 'fr' ? ' ? Cette action est irréversible.' : '? This action cannot be undone.'}
              </p>
            </div>
            <div className="flex items-center gap-3 px-6 pb-6">
              <button onClick={closeModal} className="flex-1 px-4 py-2.5 text-sm font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors">
                {t?.common?.cancel || 'Cancel'}
              </button>
              <button onClick={handleDelete} disabled={saving}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60"
                style={{ background: 'var(--color-error)' }}>
                {saving ? (language === 'fr' ? 'Suppression…' : 'Deleting…') : (t?.common?.delete || 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
